import React, { useEffect, useState } from 'react';
import { LedgerEntryLine } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Textarea } from '../common/Catalyst/textarea';
import { Text } from '../common/Catalyst/text';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  signedAmountParts,
} from '../utils/billingHelpers';
import { ledgerEntryType } from './enumLabels';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onReversed: () => void;
  entry: LedgerEntryLine | null;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Reverse a ledger entry.
 *
 * **Nothing is deleted or edited.** A reversal is a new entry pointing
 * at the one it undoes, with the opposite sign, and both stay on the
 * statement — which is why the confirm text says what will be *written*
 * rather than what will be changed.
 *
 * The reason is required by the API and it is the only explanation
 * anybody reading the ledger in a year will have.
 */
const ReverseEntryDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onReversed,
  entry,
}) => {
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setEffectiveDate('');
    setErrors({});
    setFailure(null);
  }, [isOpen]);

  const submit = async (): Promise<void> => {
    if (!entry) return;
    const found: Record<string, string> = {};
    if (!reason.trim()) {
      found.reason = 'A reason is required — it stays on the ledger.';
    }
    if (effectiveDate && !DATE.test(effectiveDate)) {
      found.effectiveDate = 'Use a date, or leave blank for today.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setSaving(true);
      setFailure(null);
      await billingService.reverseEntry({
        entryId: entry.id,
        reverseRequest: {
          reason: reason.trim(),
          effective_date: effectiveDate || null,
        },
      });
      onReversed();
      onClose();
    } catch (err) {
      console.error('Error reversing entry:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!entry) return null;

  const parts = signedAmountParts(entry.amount);
  const opposite = signedAmountParts(
    entry.amount.startsWith('-')
      ? entry.amount.replace(/^-/, '')
      : `-${entry.amount}`
  );

  return (
    <Dialog open={isOpen} onClose={onClose} size="xl">
      <DialogTitle>Reverse this entry</DialogTitle>
      <DialogBody>
        {failure && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle="The entry was not reversed"
              alertMessage={failure}
              isVisible={!!failure}
              onClose={() => setFailure(null)}
              timeout={14000}
              isInline
            />
          </div>
        )}

        <div className="mb-4 rounded-md border border-zinc-200 dark:border-zinc-700 p-3">
          <div className="text-sm text-zinc-500 dark:text-zinc-400">
            {ledgerEntryType.label(entry.entry_type)} ·{' '}
            {formatBillingDate(entry.effective_date)}
          </div>
          <div className="mt-1 text-zinc-900 dark:text-white">
            {entry.description}
          </div>
          <div className="mt-1 font-medium text-zinc-900 dark:text-white">
            {parts.text}
          </div>
        </div>

        <Text className="mb-4">
          This <strong>writes a new entry</strong> of {opposite.text} against
          it. Neither is removed: the original charge and its reversal both stay
          on the statement, which is how the homeowner can see what happened.
        </Text>

        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Reason</Label>
              <Description>
                Required, and permanent. In a year this is the only explanation
                anybody reading the ledger will have.
              </Description>
              <Textarea
                rows={3}
                value={reason}
                invalid={!!errors.reason}
                onChange={(e) => setReason(e.target.value)}
              />
              {errors.reason && <ErrorMessage>{errors.reason}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Effective date</Label>
              <Description>
                Blank means today. Use the original&rsquo;s date to keep the two
                together on a statement.
              </Description>
              <Input
                type="date"
                value={effectiveDate}
                invalid={!!errors.effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="max-w-[14rem]"
              />
              {errors.effectiveDate && (
                <ErrorMessage>{errors.effectiveDate}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="red" onClick={submit} disabled={saving}>
          {saving ? 'Reversing…' : `Write ${opposite.text} reversal`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReverseEntryDialog;
